import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, LifeBuoy, Phone } from "lucide-react";
import { api } from "@/lib/api";
import { Spinner } from "@/components/common/Loading";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import RelatedLinks from "@/components/common/RelatedLinks";
import { DISCLAIMER_TEXT } from "@/components/layout/Footer";

export default function Resources() {
  const [data, setData] = useState(null);
  const [active, setActive] = useState("US");

  useEffect(() => {
    api.get("/resources/crisis").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <Spinner label="Loading resources" />;

  const country = data.countries.find((c) => c.code === active) || data.countries[0];

  return (
    <div className="max-w-4xl mx-auto px-4 py-10">
      <Breadcrumbs items={[{ label: "Recovery Resources" }]} />
      <h1 className="mt-5 font-display text-3xl sm:text-4xl font-bold text-[#1F2A2E]">
        Recovery Resources &amp; Crisis Support
      </h1>
      {/* One-sentence definition first, then the crisis banner (audit: g_definition). */}
      <p
        data-testid="resources-definition"
        className="mt-3 text-[#1F2A2E] leading-7 max-w-3xl font-medium"
      >
        Recovery resources are the crisis lines, helplines and fellowship links you can use
        directly, with or without a Sponsor Me account.
      </p>
      <div className="mt-6 rounded-2xl bg-[#FBE6E4] border-2 border-[#E9B4AE] p-6">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-6 h-6 text-[#B42318] shrink-0 mt-0.5" />
          <div>
            <h2 className="font-display text-3xl font-bold text-[#7A1A12]">Need Immediate Help?</h2>
            <p data-testid="crisis-disclaimer" className="mt-3 text-[#7A2A22] leading-7">
              {data.disclaimer}
            </p>
          </div>
        </div>
      </div>

      <p className="mt-6 text-sm text-[#4B5A60]">
        Emergency and crisis information is always free on Sponsor Me. It is never behind a
        membership or a paywall.
      </p>

      <div className="mt-6 flex flex-wrap gap-2">
        {data.countries.map((c) => (
          <button
            key={c.code}
            onClick={() => setActive(c.code)}
            data-testid={`country-tab-${c.code}`}
            className={`px-3 py-2 rounded-xl text-sm font-semibold border-2 bg-white ${
              active === c.code
                ? "border-[#1F6F78] text-[#1F6F78] bg-[#F2FAFA]"
                : "border-[#E3DED4] text-[#4B5A60] hover:border-[#BFD9DB]"
            }`}
          >
            {c.country}
          </button>
        ))}
      </div>

      <div data-testid="crisis-resource-list" className="mt-5 grid gap-4 sm:grid-cols-2">
        {country.resources.map((r) => (
          <div key={r.name} className="sm-card p-5 bg-white">
            <div className="flex items-center gap-2">
              <span className="w-9 h-9 rounded-full bg-[#FBE6E4] text-[#B42318] flex items-center justify-center">
                <Phone className="w-4 h-4" />
              </span>
              <h3 className="font-display text-base font-bold text-[#1F2A2E]">{r.name}</h3>
            </div>
            <p className="mt-3 font-display text-2xl font-bold text-[#B42318]">{r.contact}</p>
            <p className="mt-1 text-sm text-[#4B5A60]">{r.note}</p>
          </div>
        ))}
      </div>

      <div className="mt-8 sm-card p-6 bg-white">
        <div className="flex items-center gap-2">
          <LifeBuoy className="w-5 h-5 text-[#1F6F78]" />
          <h2 className="font-display text-xl font-bold text-[#1F2A2E]">
            What peer sponsorship is, and is not
          </h2>
        </div>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div className="rounded-xl bg-[#E4F3EA] p-4">
            <p className="font-bold text-sm text-[#14603C]">A sponsor can</p>
            <ul className="mt-2 space-y-1.5 text-sm text-[#1F7A4D]">
              <li>Share their own recovery experience</li>
              <li>Help you understand the steps or program tools</li>
              <li>Be someone to call when things get hard</li>
              <li>Encourage you to build a recovery network</li>
            </ul>
          </div>
          <div className="rounded-xl bg-[#FBE6E4] p-4">
            <p className="font-bold text-sm text-[#7A1A12]">A sponsor cannot</p>
            <ul className="mt-2 space-y-1.5 text-sm text-[#B42318]">
              <li>Provide medical or psychiatric care</li>
              <li>Diagnose or treat any condition</li>
              <li>Respond to emergencies or crises</li>
              <li>Replace professional treatment or therapy</li>
            </ul>
          </div>
        </div>
        <p className="mt-4 text-sm text-[#4B5A60] leading-7">
          Weighing this up against a recovery app?{" "}
          <Link
            to="/vs/ai-sponsor"
            data-testid="resources-vs-ai-link"
            className="font-semibold text-[#1F6F78] hover:underline"
          >
            See how a human peer sponsor compares with an AI recovery companion
          </Link>
          .
        </p>
      </div>

      <div className="mt-6 rounded-2xl bg-[#F2EFE8] border border-[#E3DED4] p-5">
        <h3 className="font-display text-base font-bold text-[#1F2A2E]">
          Independent Recovery Community
        </h3>
        <p className="mt-1.5 text-sm text-[#4B5A60] leading-6">{DISCLAIMER_TEXT}</p>
      </div>
      <RelatedLinks
        heading="Keep reading"
        links={[
          { to: "/sponsors", label: "Browse peer sponsors" },
          { to: "/vs/ai-sponsor", label: "Human sponsors vs AI recovery apps" },
          { to: "/about", label: "About Sponsor Me" },
        ]}
      />
    </div>
  );
}
